// What the signed-in person pays Greenback for, held once for the whole app
// (PRD 114 §4.4).
//
// A MODULE STORE, NOT A CONTEXT. The sidebar badge reads it, and WebNavigation
// sits in AppShell, above BusinessProvider; the paused banner and the billing
// card read it from inside the business shell; /subscription reads it outside
// both. A module store is the house answer for state that has to cross that
// line (businessExperienceStore, paidBusinessStore).
//
// WHEN IT ASKS. Once per sign-in, when the shell mounts the badge; again on a
// business switch (BusinessContext), on window focus once five minutes have
// passed, when /subscription is restored from the back-forward cache after
// Stripe's portal, and after a purchase lands (the billing card, back from
// Checkout). A full page load (Stripe's portal returns with one) starts the
// store empty, so that asks afresh by itself. Only one ask is ever on its way:
// a refresh while one is answers with that one, except after a change, which
// waits for it and then asks once more, since it left before the change.
//
// A FAILED REQUEST LEAVES TODAY'S SCREENS. The web deploys before the api that
// serves this list, and a missing endpoint is a 404: with no list the page and
// the badge render exactly what they did before it existed, and every button
// that would deep-link into the new page keeps its old target. A refresh that
// fails after a good answer keeps that answer rather than flipping the screens
// back and forth. Why there is no list is kept (`reason`): a 404 is an api
// that does not serve it yet, and says nothing; anything else (a 5xx, the
// network) is a failure /subscription says, with a retry.

import { BillingAccounts, billingAccountsService } from './billingAccountsService';
import { SubscriptionApiError } from './subscriptionService';

/** How long a window focus leaves the list alone before asking again. */
export const BILLING_ACCOUNTS_FOCUS_REFRESH_MS = 5 * 60 * 1000;

export interface BillingAccountsState {
  /** Whose list this is. */
  uuid: string | undefined;
  /**
   * `loading` before the first answer; `ready` once a list arrived (kept
   * through later failures); `unavailable` when none ever did.
   */
  status: 'idle' | 'loading' | 'ready' | 'unavailable';
  /** The last list that arrived for this user, or null. */
  data: BillingAccounts | null;
  /**
   * Why there is no list, once `unavailable`: `not_served` for an api that
   * does not serve it yet (404), `failed` for anything else.
   */
  reason?: 'not_served' | 'failed';
}

/** A 404 is an api that does not serve the list yet; anything else failed. */
const unavailableReason = (error: unknown): NonNullable<BillingAccountsState['reason']> =>
  error instanceof SubscriptionApiError && error.status === 404 ? 'not_served' : 'failed';

type Listener = (state: BillingAccountsState) => void;

const EMPTY: BillingAccountsState = { uuid: undefined, status: 'idle', data: null };

let state: BillingAccountsState = EMPTY;
let inFlight = false;
// The ask on its way, for a refresh after a change to wait on.
let inFlightAsk: Promise<void> | null = null;
let lastAskedAt = 0;
// Bumped by every reset, so an answer that lands after sign-out (or after the
// next person signs in) is dropped rather than published as theirs.
let generation = 0;
const listeners = new Set<Listener>();

function publish(next: BillingAccountsState): void {
  state = next;
  listeners.forEach(listener => listener(state));
}

export function getBillingAccountsState(): BillingAccountsState {
  return state;
}

export function subscribeBillingAccounts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Resolves once this ask has settled (at once when one is already in flight). */
function ask(uuid: string): Promise<void> {
  if (inFlight) return Promise.resolve();
  inFlight = true;
  lastAskedAt = Date.now();
  const asked = generation;
  if (state.status === 'idle') publish({ ...state, status: 'loading' });

  const asking = billingAccountsService
    .list()
    .then(
      data => ({ data, ok: true as const }),
      (error: unknown) => ({ data: null, ok: false as const, error })
    )
    .then(result => {
      if (asked !== generation || state.uuid !== uuid) return;
      inFlight = false;
      inFlightAsk = null;
      if (result.ok) {
        publish({ uuid, status: 'ready', data: result.data });
      } else if (state.status !== 'ready') {
        publish({ uuid, status: 'unavailable', data: null, reason: unavailableReason(result.error) });
      }
    });
  inFlightAsk = asking;
  return asking;
}

/** Ask once for this user; a second caller, or a later mount, reuses the answer. */
export function ensureBillingAccountsLoaded(uuid: string | undefined): void {
  if (!uuid) return;
  if (state.uuid !== uuid) {
    generation += 1;
    inFlight = false;
    inFlightAsk = null;
    lastAskedAt = 0;
    publish({ uuid, status: 'idle', data: null });
  }
  if (state.status === 'idle') void ask(uuid);
}

/**
 * Ask again now: after a business switch, a return from Stripe's portal, or
 * the page's "Try again". Nothing to refresh while no list was ever asked for
 * (signed out, or a screen that mounted before the shell); the shell's own ask
 * comes next. Resolves once the answer has landed.
 */
export function refreshBillingAccounts(): Promise<void> {
  if (!state.uuid) return Promise.resolve();
  return ask(state.uuid);
}

/**
 * Ask again after something the list shows has changed: a purchase that just
 * landed (the billing card, back from Checkout). An ask already on its way
 * left before the change, so its answer may be the old bill: wait for it, then
 * ask once more. Resolves once that answer has landed; nothing for nobody
 * signed in, or for one who signed out meanwhile.
 */
export async function refreshBillingAccountsAfterChange(): Promise<void> {
  const { uuid } = state;
  if (!uuid) return;
  if (inFlight && inFlightAsk) {
    await inFlightAsk;
    if (state.uuid !== uuid) return;
  }
  await ask(uuid);
}

/** Ask again if the last ask is older than `maxAgeMs`: the window-focus refresh. */
export function refreshBillingAccountsIfStale(maxAgeMs: number = BILLING_ACCOUNTS_FOCUS_REFRESH_MS): void {
  if (!state.uuid || Date.now() - lastAskedAt < maxAgeMs) return;
  void ask(state.uuid);
}

/** Forget everything: on sign-out, so the next person never sees this one's bill. Tests too. */
export function resetBillingAccounts(): void {
  generation += 1;
  inFlight = false;
  inFlightAsk = null;
  lastAskedAt = 0;
  publish(EMPTY);
}

/** Tests only: also drops the listeners. */
export function resetBillingAccountsStore(): void {
  resetBillingAccounts();
  listeners.clear();
}
